import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

/**
 * Optimized custom hook for loading camera snapshots with fallback mechanism and memory management
 * @param {string} cameraId - The camera ID
 * @param {number} refreshKey - Key to trigger refresh
 * @param {Object} options - Configuration options
 * @returns {Object} - { src, loading, failed, retryCount }
 */
const useSnapshotWithFallback = (cameraId, refreshKey = 0, options = {}) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Use refs for better cleanup management
  const mountedRef = useRef(true);
  const currentBlobRef = useRef(null);
  const timeoutRef = useRef(null);
  const controllerRef = useRef(null);

  const {
    maxRetries = 3, // Reduced from 6 for better performance
    gifTimeout = 10000, // Reduced timeout
    jpegTimeout = 5000, // Reduced timeout
    gifDuration = 2, // Reduced duration for faster loading
    gifFps = 10 // Reduced FPS for smaller files
  } = options;

  // Cleanup function to revoke blob URLs
  const cleanupBlob = useCallback(() => {
    if (currentBlobRef.current) {
      URL.revokeObjectURL(currentBlobRef.current);
      currentBlobRef.current = null;
    }
  }, []);

  // Cancel any ongoing requests
  const cancelRequest = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    // Reset state when refreshKey changes
    console.log(`🔄 useSnapshotWithFallback: refreshing camera ${cameraId} with refreshKey ${refreshKey}`)
    setFailed(false);
    setLoading(true);
    setSrc(null);
    setRetryCount(0);

    // Cleanup previous blob and requests
    cleanupBlob();
    cancelRequest();

    const loadGifSnapshot = async (attempt = 0) => {
      if (!mountedRef.current) return;
      
      try {
        setFailed(false);
        setLoading(true);
        
        // Cleanup previous blob
        cleanupBlob();

        // Create new abort controller for this request
        controllerRef.current = new AbortController();

        // Retry logic with increasing timeouts and varying parameters
        const timeout = gifTimeout + (attempt * 2000); // Reduced increment
        const duration = Math.max(1, Math.min(3, 3 - Math.floor(attempt / 2))); // Shorter duration
        const fps = Math.max(6, gifFps - attempt); // Lower minimum FPS
        
        const resp = await api.get(`/cameras/${cameraId}/snapshot-gif`, {
          responseType: 'blob',
          signal: controllerRef.current.signal,
          headers: { 
            Accept: 'image/gif'
          },
          params: {
            duration,
            fps,
            t: refreshKey > 0 ? refreshKey : Date.now(),
            refresh: refreshKey || 0,
            _cb: Math.random(),
            quality: 'medium' // Add quality parameter for smaller files
          },
          timeout,
        });

        // Validate response
        if (!resp.data || resp.data.size === 0) {
          throw new Error('Empty response from server');
        }

        const blobUrl = URL.createObjectURL(resp.data);
        currentBlobRef.current = blobUrl;
        
        if (mountedRef.current) {
          setSrc(blobUrl);
          setLoading(false);
          setRetryCount(0);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('GIF request was cancelled');
          return;
        }
        
        console.error(`Failed to load GIF snapshot (attempt ${attempt + 1}):`, error.message);
        
        if (mountedRef.current) {
          // Retry up to maxRetries times with delays
          if (attempt < maxRetries) {
            setRetryCount(attempt + 1);
            timeoutRef.current = setTimeout(() => {
              if (mountedRef.current) loadGifSnapshot(attempt + 1);
            }, 1000 + (attempt * 500)); // Reduced retry delay
          } else {
            // Try fallback to regular snapshot
            await loadRegularSnapshot();
          }
        }
      }
    };

    const loadRegularSnapshot = async () => {
      if (!mountedRef.current) return;
      
      try {
        console.log('Trying fallback to regular snapshot...');
        
        // Create new abort controller for this request
        controllerRef.current = new AbortController();
        
        const resp = await api.get(`/cameras/${cameraId}/snapshot`, {
          responseType: 'blob',
          signal: controllerRef.current.signal,
          headers: { 
            Accept: 'image/jpeg'
          },
          params: {
            t: refreshKey > 0 ? refreshKey : Date.now(),
            refresh: refreshKey || 0,
            _cb: Math.random(),
            quality: 'medium' // Add quality parameter
          },
          timeout: jpegTimeout,
        });

        // Validate response
        if (!resp.data || resp.data.size === 0) {
          throw new Error('Empty response from server');
        }

        const blobUrl = URL.createObjectURL(resp.data);
        currentBlobRef.current = blobUrl;
        
        if (mountedRef.current) {
          setSrc(blobUrl);
          setLoading(false);
          setRetryCount(0);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('JPEG request was cancelled');
          return;
        }
        
        console.error('Failed to load regular snapshot:', error);
        if (mountedRef.current) {
          setFailed(true);
          setLoading(false);
          setRetryCount(0);
        }
      }
    };

    if (cameraId) {
      loadGifSnapshot();
    }

    return () => {
      mountedRef.current = false;
      cleanupBlob();
      cancelRequest();
    };
  }, [cameraId, refreshKey, maxRetries, gifTimeout, jpegTimeout, gifDuration, gifFps, cleanupBlob, cancelRequest]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      cleanupBlob();
      cancelRequest();
    };
  }, [cleanupBlob, cancelRequest]);

  return { src, loading, failed, retryCount };
};

export default useSnapshotWithFallback;
